import React, { Fragment } from 'react';
import classnames from 'classnames';
import ReactModal from 'react-modal';
import { connect } from 'react-redux';

import { changeLocation, hideMap, setCurrentArea } from 'actions/map';
import TravelFailureModal from 'components/TravelFailureModal';

import ActionRefreshContext from "components/ActionRefreshContext";
import MapComponent from 'components/Map/MapComponent';
import { IArea, IGateEvent } from "types/map";
import { IAppState } from "types/app";

import GateStoryletModal from "components/GateEventModal";
import MediaSmDown from "components/Responsive/MediaSmDown";
import CloseButton from "components/Map/CloseButton";
import MapContext from "components/Map/MapContext";
import ActionRefreshModal from "components/ActionRefreshModal";
import PurchaseFateFromGateEvent from 'components/Map/PurchaseFateFromGateEvent';
import wait from 'utils/wait';
import { MAP_HEIGHT, MAP_WIDTH } from 'components/Map/constants';
import ExceptionalFriendModal from 'components/ExceptionalFriendModal';
import ExceptionalFriendModalContext from 'components/ExceptionalFriendModal/ExceptionalFriendModalContext';
import MapService from 'services/MapService';

interface Props {
  canTravel: boolean,
  currentArea: IArea | undefined,
  dispatch: Function,
  fallbackMapPreferred: boolean,
  isMapOpen: boolean,
  isMoving: boolean,
  isVisible: boolean,
}

interface State {
  cachedMapCenter: number[],
  cachedZoomLevel: number,
  gateEvent?: IGateEvent | undefined,
  isActionRefreshModalOpen: boolean,
  isChangingArea: boolean,
  isExceptionalFriendModalOpen: boolean,
  isFetchingUpdatedMapData: boolean,
  isGateStoryletModalOpen: boolean,
  isPurchaseFateModalOpen: boolean,
  isModalOpen: boolean,
  message?: string | undefined,
}

const MINIMUM_TRAVEL_DURATION_MILLISECONDS = 800;

class MapContainer extends React.Component<Props, State> {

  state = {
    cachedMapCenter: [MAP_WIDTH / 2, MAP_HEIGHT / 2], // default
    cachedZoomLevel: 3, // default
    gateEvent: undefined,
    isActionRefreshModalOpen: false,
    isChangingArea: false,
    isExceptionalFriendModalOpen: false,
    isFetchingUpdatedMapData: false,
    isGateStoryletModalOpen: false,
    isPurchaseFateModalOpen: false,
    isModalOpen: false,
    message: undefined,
  };

  handleAreaClick = async (area: IArea) => {
    const { currentArea, dispatch, isMoving } = this.props;
    const { isChangingArea } = this.state;

    if (isChangingArea) {
      return;
    }

    // We don't know where we are
    if (currentArea === undefined) {
      return;
    }

    // If we're currently moving or trying to move to the same place, then return early
    if (isMoving || area.id === currentArea.id) {
      return;
    }

    const { gateEvent, id, unlocked } = area;

    if (unlocked) {
      const startAt = window.performance.now();

      // Store a reference to this, in case changing area fails and we need to roll back
      const previousArea = currentArea;

      this.setState({ isChangingArea: true });
      // Optimistically move the player's marker to the destination
      dispatch(setCurrentArea(area));

      // Wait for the server response
      const { isSuccess, message } = await dispatch(changeLocation(id, { closeMap: false }));

      // On success, wait for transitions to complete, then hide the map
      if (isSuccess) {
        // We want to spend at least 800ms in the window, to allow transitions to settle
        const elapsedTravelTime = window.performance.now() - startAt;
        const travelTimeRemaining = MINIMUM_TRAVEL_DURATION_MILLISECONDS - elapsedTravelTime;
        if (travelTimeRemaining > 20) {
          await wait(travelTimeRemaining);
        }

        this.setState({ isChangingArea: false });

        // We have successfully moved; close up the map
        this.handleRequestCloseMap();
        return;
      }

      // On failure, restore the marker to where we were, and show the message we received
      dispatch(setCurrentArea(previousArea));
      this.setState({
        message,
        isChangingArea: false,
        isModalOpen: true,
      });
    }

    // If we're locked, with a gate storylet, then enter it
    if (!unlocked) {
      if (gateEvent) {
        this.setState({ gateEvent, isGateStoryletModalOpen: true });
      }
    }

    return;
  };

  handleMapWillUnmount = (zoom: number, center: number[]) => {
    this.setState({
      cachedMapCenter: center,
      cachedZoomLevel: zoom,
    });
  };

  handleOpenActionRefreshModal = () => {
    this.setState({ isActionRefreshModalOpen: true });
  };

  handleOpenPurchaseFateModal = () => {
    this.setState({ isPurchaseFateModalOpen: true });
  };

  handleRequestCloseActionRefreshModal = () => {
    this.setState({ isActionRefreshModalOpen: false });
  };

  handleRequestCloseExceptionalFriendModal = async (didUserSubscribe: boolean) => {
    const { gateEvent, isGateStoryletModalOpen } = this.state;
    // Close the modal
    this.setState({ isExceptionalFriendModalOpen: false });

    // If the user actually subscribed from within the modal, we need to update the map and gate event
    if (didUserSubscribe) {
      this.setState({ isFetchingUpdatedMapData: true });
      const { data: { areas } } = await (new MapService()).fetch();
      // If the gate event modal is open and has gate event data, update state
      if (isGateStoryletModalOpen && gateEvent) {
        // We need to cast to IGateEvent because TS thinks gateEvent is a 'never'
        const gateEventID = (gateEvent! as IGateEvent).id;
        const gatedArea = (areas as IArea[]).find((area) => area.gateEvent?.id === gateEventID);
        if (gatedArea) {
          this.setState({ gateEvent: gatedArea.gateEvent });
        }
      }
      this.setState({ isFetchingUpdatedMapData: false });
    }
  };

  handleRequestCloseFailureModal = () => {
    this.setState(s => ({ ...s, isModalOpen: false }));
  };

  handleRequestCloseMap = () => {
    const { dispatch } = this.props;
    dispatch(hideMap());
  };

  handleRequestOpenGateStorylet = (area: IArea) => {
    const { unlocked, gateEvent } = area;
    if (unlocked) {
      return;
    }

    // Try beginning the storylet

    this.setState({ gateEvent, isGateStoryletModalOpen: true });
  };

  handleRequestCloseGateStorylet = async (shouldAutoClose?: boolean) => {
    const { dispatch } = this.props;
    this.setState({ isGateStoryletModalOpen: false });
    if (shouldAutoClose) {
      await wait(500);
      dispatch(hideMap());
    }
  };

  handleRequestClosePurchaseFateModal = () => {
    this.setState({ isPurchaseFateModalOpen: false });
  };

  renderContent = () => {
    const {
      canTravel,
      fallbackMapPreferred,
      isMapOpen,
      isVisible,
    } = this.props;

    const {
      cachedMapCenter,
      cachedZoomLevel,
      gateEvent,
      isChangingArea,
      isExceptionalFriendModalOpen,
      isFetchingUpdatedMapData,
      isGateStoryletModalOpen,
    } = this.state;

    return (
      <Fragment>
        <ExceptionalFriendModalContext.Provider
          value={{
            openModal: () => this.setState({ isExceptionalFriendModalOpen: true }),
            onRequestClose: this.handleRequestCloseExceptionalFriendModal,
          }}
        >

          <MediaSmDown>
            {isMapOpen && <CloseButton onClick={this.handleRequestCloseMap} />}
          </MediaSmDown>
          <ReactModal
            onRequestClose={this.handleRequestCloseMap}
            isOpen={canTravel && isVisible}
            closeTimeoutMS={200}
            bodyOpenClassName="ReactModal__Body--open-map"
            className={classnames(
              "modal--map__content",
              fallbackMapPreferred && 'modal--map__content--fallback'
            )}
            style={{
              overlay: {
                alignItems: 'center',
                backgroundColor: 'rgba(0, 0, 0, 0.65)',
                display: 'flex',
                justifyContent: 'center',
              },
              content: (fallbackMapPreferred ? {
                overflowX: 'hidden',
                overflowY: 'hidden',
                padding: 0,
              } : {})
            }}
          >
            <MapContext.Provider value={{
              isGateStoryletModalOpen,
              onRequestOpenGateStoryletModal: this.handleRequestOpenGateStorylet,
              onRequestCloseGateStoryletModal: this.handleRequestCloseGateStorylet,
            }}>
              <MapComponent
                initialCenter={cachedMapCenter}
                initialZoom={cachedZoomLevel}
                isChangingArea={isChangingArea}
                onAreaClick={this.handleAreaClick}
                onWillUnmount={this.handleMapWillUnmount}
              />
            </MapContext.Provider>
          </ReactModal>
          <GateStoryletModal
            isBeingUpdated={isFetchingUpdatedMapData}
            isOpen={isGateStoryletModalOpen}
            gateEvent={gateEvent}
            onRequestClose={this.handleRequestCloseGateStorylet}
          />

          <ExceptionalFriendModal
            isOpen={isExceptionalFriendModalOpen}
            onRequestClose={this.handleRequestCloseExceptionalFriendModal}
            disableTouchEvents
          />
        </ExceptionalFriendModalContext.Provider>
      </Fragment>
    );
  };


  /**
   * Render
   * @return {Object}
   */
  render() {
    const {
      isActionRefreshModalOpen,
      isModalOpen,
      isPurchaseFateModalOpen,
      message,
    } = this.state;

    return (
      <ActionRefreshContext.Provider
        value={{
          onOpenActionRefreshModal: this.handleOpenActionRefreshModal,
          onOpenPurchaseFateModal: this.handleOpenPurchaseFateModal,
        }}
      >
        <Fragment>
          {this.renderContent()}
          <TravelFailureModal
            isOpen={isModalOpen}
            message={message}
            onRequestClose={this.handleRequestCloseFailureModal}
            disableTouchEvents
          />
          <ActionRefreshModal
            isOpen={isActionRefreshModalOpen}
            onRequestClose={this.handleRequestCloseActionRefreshModal}
            overlayClassName="modal--map-action-refresh__overlay modal--map-action-refresh__overlay"
            disableTouchEvents
          />
          <PurchaseFateFromGateEvent
            isModalOpen={isPurchaseFateModalOpen}
            onRequestClose={this.handleRequestClosePurchaseFateModal}
            style={{
              overlay: {
                zIndex: 20000,
              },
            }}
            disableTouchEvents
          />
        </Fragment>
      </ActionRefreshContext.Provider>
    );
  }
}

const mapStateToProps = ({
  map: {
    canTravel,
    currentArea,
    fallbackMapPreferred,
    isMoving,
    isVisible,
  },
}: IAppState) => ({
  canTravel,
  currentArea,
  fallbackMapPreferred,
  isMoving,
  isVisible,
  isMapOpen: canTravel && isVisible,
});

export default connect(mapStateToProps)(MapContainer);
